import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2, Sparkles } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { refreshUser } = useAuth();
  const navigate = useNavigate();

  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const didVerify = useRef(false);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMsg('No verification token found in the link.');
      return;
    }

    if (didVerify.current) return;
    didVerify.current = true;

    axios
      .get(`${API_BASE}/api/auth/verify-email`, { params: { token } })
      .then(async () => {
        setStatus('success');
        const updatedUser = await refreshUser().catch(() => null);
        setTimeout(() => {
          if (updatedUser?.onboarding_completed) {
            navigate('/dashboard', { replace: true });
          } else {
            navigate('/onboarding', { replace: true });
          }
        }, 2000);
      })
      .catch((err) => {
        setStatus('error');
        setErrorMsg(err.response?.data?.detail || 'Verification failed. The link may have expired.');
      });
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 dark:from-slate-950 dark:via-slate-950 dark:to-slate-900 flex items-center justify-center p-6">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/30">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-bold text-2xl">GDS</span>
          </Link>
        </div>

        <Card className="border-0 shadow-xl shadow-slate-200/50 dark:shadow-slate-900/50">
          <CardContent className="p-8 text-center space-y-4">
            {status === 'loading' && (
              <>
                <Loader2 className="w-12 h-12 text-indigo-600 dark:text-indigo-400 animate-spin mx-auto" />
                <p className="text-slate-600 dark:text-slate-300">Verifying your email…</p>
              </>
            )}

            {status === 'success' && (
              <>
                <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400 mx-auto" />
                <h2 className="text-xl font-semibold">Email verified!</h2>
                <p className="text-slate-600 dark:text-slate-400 text-sm">
                  Your email address has been confirmed. Redirecting you now…
                </p>
                <Loader2 className="w-5 h-5 text-indigo-600 dark:text-indigo-400 animate-spin mx-auto mt-2" />
              </>
            )}

            {status === 'error' && (
              <>
                <XCircle className="w-12 h-12 text-red-600 dark:text-red-400 mx-auto" />
                <h2 className="text-xl font-semibold">Verification failed</h2>
                <p className="text-slate-600 dark:text-slate-400 text-sm">{errorMsg}</p>
                <div className="flex flex-col gap-2 items-center mt-2">
                  <Link to="/dashboard" className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 text-sm">
                    Go to Dashboard
                  </Link>
                  <span className="text-slate-500 dark:text-slate-600 text-xs">
                    You can resend the verification email from your dashboard settings.
                  </span>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
